<template>
  <v-dialog v-model="visible" persistent max-width="638">
    <v-card v-if="visible" class="v-dialog--custom">
      <v-card-title class="headline pa-4">
        <span class="v-dialog--custom__title my-2">{{ $t(`${namespace}.categoryPermissionBulkTitle`) }}</span>
        <v-btn class="v-dialog--custom__close mr-2" fab flat color="gray-6" :disabled="isLoading" @click="close">
          <v-icon dark>close</v-icon>
        </v-btn>
      </v-card-title>

      <form class="form--custom" novalidate @submit.prevent="batchUpdate">
        <v-card-text class="px-4 pt-0">
          <p class="pb-2">{{ $t(`${namespace}.categoryPermissionBulkMsg`) }}</p>
          <p><strong>{{ $t(`${namespace}.categoryPermissionBulkStep1`) }}</strong></p>
          <v-radio-group
            v-model="radioGroupEmployees"
            v-validate="{ required: true }"
            :error-messages="errors.collect($t('Common.employees'))"
            :data-vv-name="$t('Common.employees')"
            required
          >
            <v-radio :label="$t(`${namespace}.allEmployees`)" value="allEmployees"></v-radio>
            <v-radio :label="$t(`${namespace}.selectEmployeesByName`)" value="perName"></v-radio>
            <v-radio :label="$t(`${namespace}.selectEmployeesById`)" value="perId"></v-radio>
          </v-radio-group>

          <AutoComplete
            v-if="radioGroupEmployees === 'perName'"
            kind="employee"
            :data="employeeIds"
            :companyId="company.id"
            :placeholder="$t('Components.ApplyToAllDialog.searchEmployees')"
            :isValid="radioGroupEmployees === 'perName' && isEmployeeValid"
            :required="radioGroupEmployees === 'perName'"
            multiple
            @input="updateEmployee"
          />

          <v-text-field
            v-if="radioGroupEmployees === 'perId'"
            v-model="employeesIdsField"
            flat
            outline
            solo
            color="accent"
            clearable
            :name="$t(`${namespace}.fieldIdsName`)"
            :placeholder="$t(`${namespace}.fieldIdsPlaceholder`)"
            v-validate="{ required: true }"
            :error-messages="errors.collect($t(`${namespace}.fieldIdsName`))"
            :data-vv-name="$t(`${namespace}.fieldIdsName`)"
            required
            @blur="stringToArray"
          />

          <p class="mb-0"><strong>{{ $t(`${namespace}.categoryPermissionBulkStep2`) }}</strong></p>
        </v-card-text>

        <CarCategoriesList
          :categories="categoriesList"
          class="mb-5"
          :required="!isCategoryIdValid"
          @getCategoriesList="updateCategoriesList"
        />

        <v-divider />

        <v-card-actions class="pa-4">
          <v-spacer />
          <a
            v-if="!isLoading"
            href="#"
            class="text--normal accent--text mr-4"
            color="accent"
            flat
            round
            @click.prevent="close"
          >
            {{ $t('Common.cancel') }}
          </a>
          <v-btn
            type="submit"
            :disabled="isLoading"
            :loading="isLoading"
            round
            depressed
            color="accent"
          >
            {{ $t(`${namespace}.categoryPermissionBulkBtn`) }}
          </v-btn>
        </v-card-actions>
      </form>
    </v-card>
  </v-dialog>
</template>

<script>
import { mapState, mapActions } from 'vuex'
import { isArray, objectToArray } from 'utils/functions'
import AutoComplete from 'components/AutoComplete'
import CarCategoriesList from 'components/employees/CarCategoriesList'

export default {
  name: 'CategoryPermissionBulk',
  props: {
    visible: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    ...mapState({
      company: state => state.company.selectedCompany
    }),

    categoriesList () {
      /* todo: ridesCategoriesList */
      return objectToArray(this.$store.state.commonConfig.categories).map(item => item.value)
    }
  },
  components: {
    AutoComplete,
    CarCategoriesList
  },
  data () {
    return {
      namespace: 'Components.ApplyToAllDialog',
      isLoading: false,
      radioGroupEmployees: null,

      // custom validation
      isEmployeeValid: true,
      isCategoryIdValid: true,

      // fields
      employeeIds: [],
      employeesIdsField: null,
      categoryIds: []
    }
  },
  methods: {
    ...mapActions({
      successAlert: 'snackbarAlert/success',
      errorAlert: 'snackbarAlert/error'
    }),

    isValidArray (val) {
      return isArray(val)
    },

    resetEmployeeOptions () {
      this.employeeIds = []
      this.employeesIdsField = null
    },

    resetAllFields () {
      this.resetEmployeeOptions()
      this.isEmployeeValid = true
      this.isCategoryIdValid = true
      this.radioGroupEmployees = null
      this.categoryIds = []
    },

    stringToArray () {
      if (!this.employeesIdsField) return
      const arr = this.employeesIdsField.replace(/[[A-zÀ-ú]-\s._]/g, '').split(',')
      this.employeeIds = arr.map(item => Number(item))
    },

    close () {
      this.$emit('close')
    },

    updateEmployee (val) {
      if (!this.isValidArray(val)) {
        this.employeeIds = []
        this.radioGroupEmployees !== 'allEmployees' && (this.isEmployeeValid = false)
        return
      }

      this.employeeIds = val
      this.radioGroupEmployees !== 'allEmployees' && (this.isEmployeeValid = true)
    },

    updateCategoriesList (val) {
      if (!this.isValidArray(val)) {
        this.categoryIds = []
        this.isCategoryIdValid = false
        return
      }

      this.categoryIds = val
      this.isCategoryIdValid = true
    },

    async batchUpdate () {
      if (this.radioGroupEmployees !== 'allEmployees' && !this.employeeIds.length) {
        this.isEmployeeValid = false
      }

      if (!this.categoryIds.length) {
        this.isCategoryIdValid = false
      }

      try {
        const isValid = await this.$validator.validateAll()

        if (this.radioGroupEmployees !== 'allEmployees' && !this.employeeIds.length) return

        if (isValid && this.categoryIds.length) {
          this.isLoading = true

          // WORKAROUND
          let ids = null

          if (this.radioGroupEmployees === 'perName') {
            const employeeIds = this.employeeIds.map(item => item.id)
            ids = this.employeeIds.length ? employeeIds : null
          } else {
            ids = this.employeeIds
          }

          await this.$apiService.batchUpdate(
            this.company.id,
            { budget: null },
            { budgetDate: null },
            { budgetPeriod: 'monthly' },
            { credit: null },
            { useBudget: null },
            { useRideCredit: null },
            { categoryIds: this.categoryIds },
            { supervisorId: null },
            { thirdPartyWebCallPermission: null },
            { webCallPermission: null },
            { ids }
          )

          this.successAlert(this.$t(`${this.namespace}.categoryPermissionBulkSuccess`))
          this.resetAllFields()
          this.close()
          this.isLoading = false
        }
      } catch (err) {
        console.log('Error', err)
        this.errorAlert(this.$t(`${this.namespace}.error`))
        this.isLoading = false
      }
    }
  },
  watch: {
    radioGroupEmployees () {
      this.resetEmployeeOptions()
    },

    employeesIdsField (val) {
      if (!val) {
        this.resetEmployeeOptions()
      }
    },

    visible (val) {
      // fix validation error when close dialog
      this.$validator._paused = !val

      this.resetAllFields()

      this.isLoading = false
      this.radioGroupEmployees = null

      // fields
      this.employeeIds = []
      this.employeesIdsField = null
      this.categoryIds = []

      val && this.$ga.page('/dialog/colaboradores/permissoes-de-categorias-em-massa')
    }
  }
}
</script>
